import React, { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import {
  fetchEmployeeAttendance,
  downloadAttendanceExport,
  AttendanceRecord,
} from "../../../../core/api/downloadAttendance";
import { useAuthToken } from "../../../../core/data/redux/useAuthToken";
import { toast } from "react-toastify";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const AttendanceTablePage: React.FC = () => {
  const empToken = useAuthToken();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<"month" | "date">("month");
  const [selectedMonth, setSelectedMonth] = useState<number>(dayjs().month());
  const [selectedYear, setSelectedYear] = useState<number>(dayjs().year());
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [employees, setEmployees] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

  // Generate days with Sunday check
  const daysInMonth = Array.from(
    { length: dayjs(`${selectedYear}-${selectedMonth + 1}-01`).daysInMonth() },
    (_, i) => {
      const dayNum = i + 1;
      const date = dayjs(`${selectedYear}-${selectedMonth + 1}-${dayNum}`);
      return {
        key: String(dayNum).padStart(2, "0"),
        label: String(dayNum),
        isSunday: date.day() === 0,
      };
    }
  );

  const filteredEmployees = employees.filter(
    (emp) =>
      emp["Employee Name"].toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp["Employee ID"].toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTotalPresent = (emp: AttendanceRecord) => {
    return daysInMonth.reduce((total, d) => {
      const status = emp[d.key];
      if (d.isSunday && !status) return total;
      return total + (status === "✔" ? 1 : 0) + (status === "HALF" ? 0.5 : 0);
    }, 0);
  };

  // API fetch
  const fetchAttendance = async () => {
    if (!empToken) return;
    setLoading(true);
    try {
      const rawData = await fetchEmployeeAttendance(
        { month: selectedMonth + 1, year: selectedYear },
        empToken
      );
      setEmployees(rawData);
      setSelectedEmployeeIds([]);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear, empToken]);

  // Selection handlers
  const toggleSelectEmployee = (id: string) => {
    setSelectedEmployeeIds((prev) =>
      prev.includes(id) ? prev.filter((eid) => eid !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedEmployeeIds.length === filteredEmployees.length) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(
        filteredEmployees.map((emp) => emp["Employee ID"])
      );
    }
  };

  // Download handler
  const handleDownload = async () => {
    if (!empToken) return;
    if (selectedEmployeeIds.length === 0) {
      toast.warning("Please select at least one employee to download.");
      return;
    }

    try {
      if (selectedEmployeeIds.length === 1) {
        await downloadAttendanceExport(
          {
            month: selectedMonth + 1,
            year: selectedYear,
            employee_id: +selectedEmployeeIds[0],
            employee_ids: [],
          },
          empToken
        );
        toast.success("Attendance downloaded successfully!");
      } else {
        await downloadAttendanceExport(
          {
            month: selectedMonth + 1,
            year: selectedYear,
            employee_ids: selectedEmployeeIds.map((id) => +id),
          },
          empToken
        );
        toast.success(
          "Attendance for selected employees downloaded successfully!"
        );
      }
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download attendance. Please try again.");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Attendance Table</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.attendanceDash}>
                    <i className="ti ti-arrow-left me-1"></i> Back
                  </Link>
                </li>
              </ol>
            </nav>
          </div>
          {/* Controls */}

          <div className="d-flex align-items-center gap-2 flex-wrap">
            {viewMode === "month" ? (
              <div className="dropdown">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm flex-fill"
                  onClick={() => setIsMonthDropdownOpen(!isMonthDropdownOpen)}
                >
                  <i className="ti ti-calendar-month me-2" />
                  {monthNames[selectedMonth]} - {selectedYear}
                </button>
                {isMonthDropdownOpen && (
                  <div
                    className="dropdown-menu show p-3"
                    style={{ minWidth: "300px" }}
                  >
                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Select Month:
                      </label>
                      <div className="d-flex flex-wrap gap-1">
                        {monthNames.map((month, idx) => (
                          <button
                            key={month}
                            type="button"
                            onClick={() => {
                              setSelectedMonth(idx);
                              setIsMonthDropdownOpen(false);
                            }}
                            className={`btn btn-sm ${
                              selectedMonth === idx
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                          >
                            {month}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="form-label fw-semibold">Year:</label>
                      <input
                        type="number"
                        className="form-control form-control-sm"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(+e.target.value)}
                        min={2020}
                        max={2030}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <ReactDatePicker
                selected={selectedDate ? selectedDate.toDate() : null}
                onChange={(date) => setSelectedDate(date ? dayjs(date) : null)}
                customInput={
                  <button className="btn btn-primary btn-sm" type="button">
                    <i className="ti ti-calendar me-2" />
                    {selectedDate
                      ? selectedDate.format("DD-MM-YYYY")
                      : "Select Date"}
                  </button>
                }
              />
            )}

            <button
              type="button"
              className="btn btn-primary btn-sm flex-fill"
              onClick={handleDownload}
            >
              <i className="ti ti-download me-2" />
              Download
            </button>
            <div className="ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        {/* Attendance Table */}
        <div className="card mt-3">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Attendance Table</h5>
            <input
              type="text"
              placeholder="Search name or ID..."
              className="form-control form-control-sm"
              style={{ width: 200 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-5 text-muted">
                No attendance records found
              </div>
            ) : (
              <div
                className="table-responsive"
                style={{
                  maxHeight: "500px",
                  overflowX: "auto",
                  overflowY: "auto",
                }}
              >
                <table className="table table-bordered table-hover align-middle mb-0">
                  <thead
                    className="table-light"
                    style={{ position: "sticky", top: 0, zIndex: 4 }}
                  >
                    <tr>
                      <th
                        style={{
                          position: "sticky",
                          left: 0,
                          zIndex: 5,
                          background: "#f8f9fa",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedEmployeeIds.length ===
                              filteredEmployees.length &&
                            filteredEmployees.length > 0
                          }
                          onChange={selectAll}
                        />
                      </th>
                      <th
                        style={{
                          position: "sticky",
                          left: 50,
                          zIndex: 5,
                          background: "#f8f9fa",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        S.No
                      </th>
                      <th
                        style={{
                          position: "sticky",
                          left: 100,
                          zIndex: 5,
                          background: "#f8f9fa",
                          minWidth: "150px",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        Employee Name
                      </th>
                      <th
                        style={{
                          position: "sticky",
                          left: 250,
                          zIndex: 5,
                          background: "#f8f9fa",
                          minWidth: "120px",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        Employee ID
                      </th>
                      {daysInMonth.map((d) => (
                        <th
                          key={d.key}
                          className={d.isSunday ? "bg-warning text-dark" : ""}
                          style={{
                            minWidth: "40px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            writingMode: d.isSunday
                              ? "vertical-rl"
                              : "horizontal-tb",
                            transform: d.isSunday ? "rotate(180deg)" : "none",
                            boxShadow: d.isSunday
                              ? "2px 0 5px rgba(0,0,0,0.1)"
                              : undefined,
                          }}
                        >
                          {d.label}
                        </th>
                      ))}
                      <th
                        style={{
                          position: "sticky",
                          right: 0,
                          zIndex: 5,
                          background: "#f8f9fa",
                          verticalAlign: "middle",
                          textAlign: "center",
                        }}
                      >
                        Total Present
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp, idx) => (
                      <tr key={emp["Employee ID"]}>
                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            background: "#fff",
                            zIndex: 3,
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedEmployeeIds.includes(
                              emp["Employee ID"]
                            )}
                            onChange={() =>
                              toggleSelectEmployee(emp["Employee ID"])
                            }
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            left: 50,
                            background: "#fff",
                            zIndex: 3,
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            left: 100,
                            background: "#fff",
                            zIndex: 3,
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {emp["Employee Name"]}
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            left: 250,
                            background: "#fff",
                            zIndex: 3,
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {emp["Employee ID"]}
                        </td>
                        {daysInMonth.map((d) => {
                          const status = emp[d.key] || "";
                          const isWeekOff = d.isSunday;
                          let displayStatus = status;
                          let colorClass = "";
                          const cellStyle: React.CSSProperties = {
                            textAlign: "center",
                            verticalAlign: "middle",
                            writingMode: isWeekOff
                              ? "vertical-rl"
                              : "horizontal-tb",
                            transform: isWeekOff ? "rotate(180deg)" : "none",
                            textOrientation: "mixed",
                            boxShadow: isWeekOff
                              ? "2px 0 5px rgba(0,0,0,0.1)"
                              : undefined,
                          };

                          if (isWeekOff && !status) {
                            displayStatus = "WEEK OFF";
                            colorClass = "text-dark fw-bold";
                          } else if (isWeekOff && status === "✔") {
                            // Employee present on week off - show green tick
                            displayStatus = "✔";
                            colorClass = "text-success fw-bold";
                          } else {
                            colorClass =
                              status === "✔"
                                ? "text-success fw-bold"
                                : status === "X"
                                ? "text-danger fw-bold"
                                : status === "HALF"
                                ? "text-warning fw-bold"
                                : "";
                          }

                          return (
                            <td
                              key={d.key}
                              className={`${colorClass} ${
                                isWeekOff ? "bg-warning bg-opacity-25" : ""
                              }`}
                              style={cellStyle}
                            >
                              {displayStatus}
                            </td>
                          );
                        })}
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            background: "#fff",
                            zIndex: 3,
                            fontWeight: "bold",
                            verticalAlign: "middle",
                            textAlign: "center",
                          }}
                        >
                          {getTotalPresent(emp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTablePage;

